import type { CalendarFilterDateType } from '@vrplatform/components';
import { day, hasValue } from '@vrplatform/utils';
import {
  type PaymentsFilterParams,
  wherePayments,
} from '@vrplatform/ui-common';
import { useCallback, useMemo } from 'react';
import { usePaymentFilter } from './PaymentFilter';
import { useTeam } from '@vrplatform/data';

export function useWherePaymentsBase(input?: PaymentsFilterParams) {
  const [{ id: currentTeamId }] = useTeam();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const filter = useMemo(
    () =>
      wherePayments({
        ...input,
        tenantId: currentTeamId,
      }),
    [JSON.stringify(input), currentTeamId]
  );
  return filter;
}

export function useWherePayments() {
  const filter = useFormattedPaymentFilter();

  return useWherePaymentsBase(filter);
}

export function useFormattedPaymentFilter(): PaymentsFilterParams {
  const { filter } = usePaymentFilter();
  const [{ id: currentTeamId }] = useTeam();

  const getDate = useCallback(
    (
      date: string | CalendarFilterDateType | undefined | null
    ): PaymentsFilterParams['date'] => {
      if (!date) return undefined;

      if (Array.isArray(date)) {
        const [start, end] = date;

        return [
          start ? day(start).toDate() : null,
          end ? day(end).toDate() : null,
        ];
      }

      if (typeof date !== 'string') return undefined;

      const [start, end] = date.split('...');

      return [
        start ? day(start).toDate() : null,
        end ? day(end).toDate() : null,
      ];
    },
    []
  );

  return useMemo(
    () => ({
      date: getDate(filter.date),
      amount: filter.amount || undefined,
      connectionName: filter.connectionId || undefined,
      connectionApp: filter.appId || undefined,
      listingIds: filter.listingIds?.filter(hasValue) || undefined,
      type2: filter.lineTypes?.filter(hasValue) || undefined,
      search: filter.search || undefined,
      automationStatus: filter.automationResult || undefined,
      listingCollectionId: filter.listingCollectionId || undefined,
      tenantId: currentTeamId,
    }),
    [
      getDate,
      filter.date,
      filter.amount,
      filter.connectionId,
      filter.appId,
      filter.listingIds,
      filter.lineTypes,
      filter.search,
      filter.automationResult,
      filter.listingCollectionId,
      currentTeamId,
    ]
  );
}
